// src/pages/admin/AttendanceManager.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import {
  getBrowserTimezone,
  utcRangeForCalendarDay,
  isoToDisplayDate,
  scheduledAtForDisplayDay,
  dayOfDateInTimezone,
  formatISOForDisplay,
  formatTimeDisplay,
  timeToUTC,
  timeFromUTC,
  COMMON_TIMEZONES,
} from '../../utils/timezoneUtils';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  X,
  Users,
  Calendar,
  Clock,
  CheckSquare,
  Square,
  SkipForward,
  Ban,
  Layers,
  Trash2,
  UserRound,
  RotateCcw,
  Copy,
  Check
} from 'lucide-react';

// ─── CopyableId ────────────────────────────────────────────────────────────────

function CopyableId({ label, value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-gray-400">{label}:</span>
      <span className="text-xs font-mono text-gray-500" title={value}>
        {value.length > 22 ? value.slice(0, 22) + '…' : value}
      </span>
      <button
        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="text-gray-300 hover:text-gray-500 transition-colors"
        title="Copy to clipboard"
      >
        {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
      </button>
    </span>
  );
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const ATTENDANCE_VALUES = ['present', 'absent', 'rescheduled'];

const ATTENDANCE_CONFIG = {
  present:     { label: 'Present',     color: 'bg-green-100 text-green-700 border-green-300',  icon: '✓', buttonClass: 'bg-green-600 hover:bg-green-700 text-white' },
  absent:      { label: 'Absent',      color: 'bg-red-100 text-red-700 border-red-300',         icon: '✗', buttonClass: 'bg-red-500 hover:bg-red-600 text-white' },
  rescheduled: { label: 'Rescheduled', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '↻', buttonClass: 'bg-yellow-500 hover:bg-yellow-600 text-white' },
  // Legacy defensive entry — trials are now managed via TrialManager.
  // Kept so any legacy enrollmentType:'trial' records in existing class rosters
  // display correctly and are never counted toward sessionsCompleted.
  trial:       { label: 'Trial',       color: 'bg-blue-100 text-blue-700 border-blue-300',      icon: '★', buttonClass: '' },
};

// ─── Date Helpers ──────────────────────────────────────────────────────────────

function toDateStr(date) {
  // "YYYY-MM-DD" in local time
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fromDateStr(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatTime(t) {
  if (!t) return '';
  const [h, mi] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${mi} ${ampm}`;
}

// Backward-compat helper -- new classes have slots[], old ones have flat dayOfWeek/time.
function getClassSlots(cls) {
  if (cls.slots && cls.slots.length > 0) return cls.slots;
  if (cls.dayOfWeek && cls.time) return [{ dayOfWeek: cls.dayOfWeek, time: cls.time }];
  return [];
}


function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function formatDisplayDate(date, timezone) {
  return date.toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    timeZone: timezone,
  });
}

// ─── Attendance Logic ──────────────────────────────────────────────────────────

// Determine the correct attendance value for a student.
// Legacy defensive: if a student somehow still has enrollmentType:'trial' in a class
// roster (from before TrialManager was introduced), record 'trial' not 'present'/'absent'
// so they are never counted toward sessionsCompleted.
function resolveAttendanceValue(student, requestedValue) {
  if (student.enrollmentType === 'trial') return 'trial';
  return requestedValue;
}

// Should this attendance value increment sessionsCompleted?
function shouldCountSession(attendanceValue) {
  return attendanceValue === 'present' || attendanceValue === 'absent';
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function AttendanceBadge({ value }) {
  if (!value) return null;
  const cfg = ATTENDANCE_CONFIG[value];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─── Reschedule Modal ──────────────────────────────────────────────────────────

function RescheduleModal({ cls, session, onConfirm, onClose, saving, conflictError, displayTimezone }) {
  const tomorrow = addDays(new Date(), 1);
  const [newDate, setNewDate] = useState(toDateStr(tomorrow));
  // Default time: convert the class's UTC time to the entry timezone for convenience
  const defaultTime = (() => {
    try { return timeFromUTC(cls.time ?? '16:00', cls.dayOfWeek ?? 'monday', displayTimezone).localTime; }
    catch { return cls.time ?? '16:00'; }
  })();
  const [newTime, setNewTime]           = useState(defaultTime);
  const [entryTimezone, setEntryTimezone] = useState(displayTimezone);
  const [error, setError] = useState(null);
  const [backDate, setBackDate] = useState(false);

  function handleConfirm() {
    if (!newDate) { setError('Please select a date.'); return; }
    if (!backDate && newDate <= toDateStr(new Date())) { setError('Reschedule date must be in the future.'); return; }
    if (!newTime) { setError('Please select a time.'); return; }
    onConfirm(newDate, newTime, entryTimezone);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Reschedule Class</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            All students will be marked as <strong>Rescheduled</strong> for the current session.
            A new makeup session will be created for the date and time below.
          </p>
          {/* Timezone selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={entryTimezone}
              onChange={e => setEntryTimezone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
              <input
                type="date"
                value={newDate}
                onChange={e => { setNewDate(e.target.value); setError(null); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                value={newTime}
                onChange={e => { setNewTime(e.target.value); setError(null); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          {/* Back-date toggle — allows creating a makeup session in the past
              (e.g. the class already happened and is being recorded after the fact). */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={backDate}
              onChange={e => { setBackDate(e.target.checked); setError(null); }}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-xs text-gray-600">
              Back-date this makeup (the class already happened). Allows a past date.
            </span>
          </label>
          {backDate && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                You're creating a makeup session in the past. Double-check the date and time.
              </p>
            </div>
          )}
          {conflictError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertTriangle size={13} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">{conflictError}</p>
            </div>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Reschedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Substitute Modal ──────────────────────────────────────────────────────────

function SubstituteModal({ session, cls, teachers, onConfirm, onClose, saving }) {
  const currentSubId = session?.substituteTeacherId ?? null;
  const [selectedId, setSelectedId] = useState(currentSubId ?? '');

  // All teachers except the original assigned teacher
  const eligibleTeachers = teachers.filter(t => t.id !== cls?.teacherId);

  function handleAssign() {
    if (!selectedId) return;
    const t = teachers.find(t => t.id === selectedId);
    onConfirm(selectedId, t?.name ?? '');
  }

  function handleRemove() {
    onConfirm(null, null);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {currentSubId ? 'Change / Remove Substitute' : 'Assign Substitute'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            The substitute will be credited for this session in payment reports.
            {cls?.teacherName && (
              <> The original teacher <strong>{cls.teacherName}</strong> will not be credited.</>
            )}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Substitute Teacher</label>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">— Select a teacher —</option>
              {eligibleTeachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            {currentSubId && (
              <button
                onClick={handleRemove}
                disabled={saving}
                className="flex-1 px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                Remove Sub
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={saving || !selectedId}
              className="flex-1 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Daily View ────────────────────────────────────────────────────────────────

function DailyView({
  date,
  classes,
  teachers,
  sessionsMap,        // { [classId]: sessionDoc } — regular sessions keyed by classId
  makeupSessions,     // [ sessionDoc, ... ] — makeup sessions on this date
  selectedStudents,   // Set of "classId__childId"
  onToggleStudent,
  onMarkStudent,
  onMarkAllPresent,
  onCancelClass,
  onRescheduleClass,
  onSelectAllInClass,
  onClearSelection,
  onDeleteSession,
  onMarkMakeupAttendance,  // (makeupSession, childIds, value) => void
  onAssignSubstitute,      // (cls, session) => void
  onClearStudentAttendance,  // (session, cls, student, isMakeup) => void
  onResetAllAttendance,      // (cls, session) => void
  displayTimezone,
}) {
  // Find classes whose UTC slot falls on this calendar day in displayTimezone
  const todayClasses = classes
    .filter(c => c.status === 'active' && getClassSlots(c).some(s =>
      scheduledAtForDisplayDay(date, displayTimezone, s.dayOfWeek, s.time) !== null
    ))
    .sort((a, b) => {
      const slotA = getClassSlots(a).find(s => scheduledAtForDisplayDay(date, displayTimezone, s.dayOfWeek, s.time) !== null);
      const slotB = getClassSlots(b).find(s => scheduledAtForDisplayDay(date, displayTimezone, s.dayOfWeek, s.time) !== null);
      return (slotA?.time ?? '').localeCompare(slotB?.time ?? '');
    });

  // Makeup sessions whose parent class is NOT on today's regular schedule
  const regularClassIds = new Set(todayClasses.map(c => c.id));
  const orphanMakeups = (makeupSessions ?? []).filter(s => !regularClassIds.has(s.classId));

  const totalSelected = selectedStudents.size;

  if (todayClasses.length === 0 && orphanMakeups.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No classes on {DAY_LABELS_FULL[date.getDay()]}</p>
        <p className="text-sm text-gray-400 mt-1">Use the arrows to navigate to a different day.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cross-class bulk action bar */}
      {totalSelected > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-purple-800">
            {totalSelected} student{totalSelected !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2 flex-wrap">
            {ATTENDANCE_VALUES.map(val => (
              <button
                key={val}
                onClick={() => onMarkStudent([...selectedStudents], val)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${ATTENDANCE_CONFIG[val].buttonClass}`}
              >
                Mark {ATTENDANCE_CONFIG[val].label}
              </button>
            ))}
          </div>
          <button
            onClick={onClearSelection}
            className="ml-auto text-xs text-purple-500 hover:text-purple-700"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Orphan makeup cards — sessions whose parent class doesn't run today */}
      {orphanMakeups.map(s => {
        const cls = classes.find(c => c.id === s.classId);
        const teacher = teachers.find(t => t.id === s.teacherId);
        return (
          <div key={s.id} className="bg-white rounded-xl border border-yellow-300 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-yellow-100 bg-yellow-50 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    {s.scheduledAt ? formatISOForDisplay(s.scheduledAt, displayTimezone) : ''}
                    <span className="text-gray-400 font-normal text-sm">{s.duration} min</span>
                    <span className="text-xs px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded-full font-medium">↻ makeup</span>
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {s.substituteTeacherId ? (
                      <>
                        <span className="text-xs font-medium text-gray-800">{s.substituteTeacherName}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-semibold">SUB</span>
                        <span className="text-xs text-gray-400">(normally {teacher?.name ?? s.teacherName})</span>
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">{teacher?.name ?? s.teacherName}</span>
                    )}
                    {cls && (() => {
                      const { localTime: clsTime, localDay: clsDay } = timeFromUTC(cls.time ?? '00:00', cls.dayOfWeek ?? 'monday', displayTimezone);
                      return <span className="text-xs text-gray-400">· normally {clsDay} at {formatTime(clsTime)}</span>;
                    })()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  s.status === 'completed'   ? 'bg-green-100 text-green-700' :
                  s.status === 'cancelled'   ? 'bg-gray-100 text-gray-500'  :
                  s.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>{s.status}</span>
                {cls && (cls.enrolledStudents ?? []).length > 0 && s.status !== 'rescheduled' && (
                  <button
                    onClick={() => onMarkMakeupAttendance(s, (cls.enrolledStudents ?? []).map(st => st.childId), 'present')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle size={13} />
                    All Present
                  </button>
                )}
                <button
                  onClick={() => onAssignSubstitute(cls ?? { id: s.classId, teacherId: s.teacherId, teacherName: s.teacherName }, s)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    s.substituteTeacherId
                      ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <UserRound size={13} />
                  {s.substituteTeacherId ? 'Change Sub' : 'Assign Sub'}
                </button>
                <button
                  onClick={() => onRescheduleClass(cls ?? { id: s.classId, teacherId: s.teacherId, teacherName: s.teacherName, time: s.scheduledAt ? new Date(s.scheduledAt).toISOString().slice(11,16) : '16:00', dayOfWeek: s.scheduledAt ? DAYS_OF_WEEK[new Date(s.scheduledAt).getUTCDay()] : 'monday', duration: s.duration }, s)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white text-xs font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                  disabled={s.status === 'rescheduled'}
                >
                  <SkipForward size={13} />
                  Reschedule
                </button>
                <button
                  onClick={() => onDeleteSession(cls ?? { id: s.classId, teacherName: s.teacherName }, s)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors"
                >
                  <Trash2 size={13} />
                  Delete
                </button>
              </div>
            </div>
            <div className="px-5 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center gap-4 flex-wrap">
              <CopyableId label="Session ID" value={s.id} />
              <CopyableId label="Class ID" value={s.classId} />
            </div>
            {/* Students enrolled in the parent class — full attendance controls */}
            {cls && (cls.enrolledStudents ?? []).length > 0 && (
              <div className="divide-y divide-gray-50">
                {(cls.enrolledStudents ?? []).map(student => {
                  const val = s.attendance?.[student.childId] ?? null;
                  const cfg = val ? ATTENDANCE_CONFIG[val] : null;
                  return (
                    <div key={student.childId} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors flex-wrap">
                      {/* Student info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-800 text-sm">{student.childName}</p>
                          {cfg && <AttendanceBadge value={val} />}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {student.parentFirstName} {student.parentLastName} · {student.parentEmail}
                        </p>
                        <p className="text-xs text-gray-400">
                          {student.sessionsCompleted ?? 0}/{student.sessionsTotal ?? 6} sessions
                        </p>
                      </div>
                      {/* Attendance buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {ATTENDANCE_VALUES.map(v => {
                          const btnCfg = ATTENDANCE_CONFIG[v];
                          const isActive = val === v;
                          return (
                            <button
                              key={v}
                              onClick={() => onMarkMakeupAttendance(s, [student.childId], v)}
                              title={btnCfg.label}
                              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all border ${
                                isActive
                                  ? `${btnCfg.color} border-current scale-110`
                                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                              }`}
                            >
                              {btnCfg.icon}
                            </button>
                          );
                        })}
                        {/* Clear button — only shown when attendance is set */}
                        {val && (
                          <button
                            onClick={() => onClearStudentAttendance(s, cls, student, true)}
                            title="Clear attendance"
                            className="w-8 h-8 rounded-lg text-xs font-bold transition-all border bg-gray-50 text-gray-400 border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200 ml-1"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Class cards */}
      {todayClasses.map(cls => {
        const teacher = teachers.find(t => t.id === cls.teacherId);
        const session = sessionsMap[cls.id] ?? null;
        const students = cls.enrolledStudents ?? [];
        const attendance = session?.attendance ?? {};

        const allInClassKeys = students.map(s => `${cls.id}__${s.childId}`);
        const classSelectedCount = allInClassKeys.filter(k => selectedStudents.has(k)).length;
        const markedCount = Object.keys(attendance).length;

        // Find the slot that falls on this display day and show its actual time in displayTimezone.
        // Use the resolved scheduledAt ISO so the displayed time reflects the DST in effect on
        // `date` (not today), which matters during DST-transition weeks.
        const matchingSlotISO = getClassSlots(cls)
          .map(s => scheduledAtForDisplayDay(date, displayTimezone, s.dayOfWeek, s.time))
          .find(iso => iso !== null);
        const displayTime = matchingSlotISO
          ? formatISOForDisplay(matchingSlotISO, displayTimezone)
          : null; // no fallback — if no matching slot this card shouldn't be rendering

        return (
          <div key={cls.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Class header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: teacher?.color ?? '#7C3AED' }}
                />
                <div>
                  <p className="font-semibold text-gray-800">
                    {displayTime ?? '-'}
                    <span className="text-gray-400 font-normal text-sm ml-2">
                      {cls.duration} min
                    </span>
                    {(cls.sessionsPerWeek ?? 1) > 1 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-600">
                        {cls.sessionsPerWeek}×/wk
                      </span>
                    )}
                    {cls.ageGroup === 'adult' && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                        Adult
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {session?.substituteTeacherId ? (
                      <>
                        <span className="text-xs font-medium text-gray-800">{session.substituteTeacherName}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded font-semibold">SUB</span>
                        <span className="text-xs text-gray-400">(normally {teacher?.name ?? cls.teacherName})</span>
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">{teacher?.name ?? cls.teacherName}</span>
                    )}
                    <span className="text-xs text-gray-400">· {cls.type} · {cls.dialect} · {cls.ageGroup}</span>
                  </div>
                </div>
              </div>

              {/* Session status badge */}
              {session && (
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  session.status === 'completed'   ? 'bg-green-100 text-green-700' :
                  session.status === 'cancelled'   ? 'bg-gray-100 text-gray-500' :
                  session.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {session.status}
                </span>
              )}

              {/* Class-level bulk actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {students.length > 0 && (
                  <>
                    <button
                      onClick={() => onSelectAllInClass(cls.id, students)}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 px-2 py-1.5 rounded-lg hover:bg-purple-50 transition-colors"
                      title="Select all in this class"
                    >
                      <CheckSquare size={13} />
                      {classSelectedCount === students.length ? 'Deselect all' : 'Select all'}
                    </button>
                    <button
                      onClick={() => onMarkAllPresent(cls)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle size={13} />
                      All Present
                    </button>
                    {/* Hide Reschedule if already rescheduled — only the makeup session can be rescheduled further */}
                    {session?.status !== 'rescheduled' && (
                      <button
                        onClick={() => onRescheduleClass(cls, session)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white text-xs font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                      >
                        <SkipForward size={13} />
                        Reschedule
                      </button>
                    )}
                    <button
                      onClick={() => onCancelClass(cls, session)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <Ban size={13} />
                      Cancel
                    </button>
                    <button
                      onClick={() => onAssignSubstitute(cls, session)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        session?.substituteTeacherId
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <UserRound size={13} />
                      {session?.substituteTeacherId ? 'Change Sub' : 'Assign Sub'}
                    </button>
                    {/* Reset All — only shown when some attendance has been saved */}
                    {session && markedCount > 0 && (
                      <button
                        onClick={() => onResetAllAttendance(cls, session)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-medium rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                        title="Reset all attendance for this session"
                      >
                        <RotateCcw size={13} />
                        Reset All
                      </button>
                    )}
                  </>
                )}
                {/* Delete session — only shown when a session doc exists */}
                {session && (
                  <button
                    onClick={() => onDeleteSession(cls, session)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white text-red-500 text-xs font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
                    title="Delete this session record"
                  >
                    <Trash2 size={13} />
                    Delete session
                  </button>
                )}
              </div>
            </div>
            <div className="px-5 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center gap-4 flex-wrap">
              <CopyableId label="Class ID" value={cls.id} />
              {session && <CopyableId label="Session ID" value={session.id} />}
            </div>

            {/* Student roster */}
            {students.length === 0 ? (
              <div className="px-5 py-4 text-sm text-gray-400 italic">
                No students enrolled. Add students in the Classes manager.
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {students.map(student => {
                  const selKey = `${cls.id}__${student.childId}`;
                  const isSelected = selectedStudents.has(selKey);
                  const currentAttendance = attendance[student.childId] ?? null;

                  return (
                    <div
                      key={selKey}
                      className={`flex items-center gap-3 px-5 py-3 transition-colors flex-wrap ${
                        isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={() => onToggleStudent(selKey)}
                        className="text-gray-400 hover:text-purple-600 flex-shrink-0 transition-colors"
                      >
                        {isSelected
                          ? <CheckSquare size={16} className="text-purple-600" />
                          : <Square size={16} />
                        }
                      </button>

                      {/* Student info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-800 text-sm">{student.childName}</p>
                          {/* Legacy defensive — shown if a pre-TrialManager trial record still exists */}
                          {student.enrollmentType === 'trial' && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                              Trial
                            </span>
                          )}
                          {currentAttendance && (
                            <AttendanceBadge value={currentAttendance} />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {student.parentFirstName} {student.parentLastName} · {student.parentEmail}
                        </p>
                        <p className="text-xs text-gray-400">
                          {student.sessionsCompleted ?? 0}/{student.sessionsTotal ?? 6} sessions
                        </p>
                      </div>

                      {/* Attendance buttons */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {ATTENDANCE_VALUES.map(val => {
                          const cfg = ATTENDANCE_CONFIG[val];
                          const isActive = currentAttendance === val ||
                            (val === 'present' && currentAttendance === 'trial');
                          return (
                            <button
                              key={val}
                              onClick={() => onMarkStudent([selKey], val)}
                              title={cfg.label}
                              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all border ${
                                isActive
                                  ? `${cfg.color} border-current scale-110`
                                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                              }`}
                            >
                              {cfg.icon}
                            </button>
                          );
                        })}
                        {/* Clear button — only shown when attendance is set */}
                        {currentAttendance && (
                          <button
                            onClick={() => onClearStudentAttendance(session, cls, student, false)}
                            title="Clear attendance (removes this mark and reverts session count)"
                            className="w-8 h-8 rounded-lg text-xs font-bold transition-all border bg-gray-50 text-gray-400 border-gray-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200 ml-1"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Session notes */}
            {session?.notes && (
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-500 italic">📝 {session.notes}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Weekly View ───────────────────────────────────────────────────────────────

function WeeklyView({ weekStart, classes, sessionsMap, teachers, onSelectDay, displayTimezone }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {days.map((day, i) => {
          const isToday = isSameDay(day, today);
          return (
            <div
              key={i}
              className={`p-3 text-center border-r border-gray-100 last:border-r-0 ${
                isToday ? 'bg-purple-50' : 'bg-gray-50'
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-wide ${
                isToday ? 'text-purple-700' : 'text-gray-500'
              }`}>
                {DAY_LABELS[dayOfDateInTimezone(day, displayTimezone) === DAYS_OF_WEEK[day.getDay()] ? day.getDay() : day.getDay()]}
              </p>
              <p className={`text-lg font-bold mt-0.5 ${
                isToday ? 'text-purple-700' : 'text-gray-800'
              }`}>
                {day.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* Class cells */}
      <div className="grid grid-cols-7 min-h-[300px]">
        {days.map((day, i) => {
          // Filter classes whose UTC slot falls on this calendar day in displayTimezone
          const dayClasses = classes
            .filter(c => c.status === 'active' && getClassSlots(c).some(s =>
              scheduledAtForDisplayDay(day, displayTimezone, s.dayOfWeek, s.time) !== null
            ))
            .sort((a, b) => {
              const slotA = getClassSlots(a).find(s => scheduledAtForDisplayDay(day, displayTimezone, s.dayOfWeek, s.time) !== null);
              const slotB = getClassSlots(b).find(s => scheduledAtForDisplayDay(day, displayTimezone, s.dayOfWeek, s.time) !== null);
              return (slotA?.time ?? '').localeCompare(slotB?.time ?? '');
            });
          const dateStr = toDateStr(day);
          const isToday = isSameDay(day, today);

          // Find makeup sessions on this date (keyed as makeup__sessionId in broadSessions)
          const makeupSessions = Object.values(sessionsMap).filter(s =>
            s.isMakeup === true &&
            (s.scheduledAt ? isoToDisplayDate(s.scheduledAt, displayTimezone) : null) === dateStr
          );

          return (
            <div
              key={i}
              onClick={() => onSelectDay(day)}
              className={`p-2 border-r border-gray-100 last:border-r-0 cursor-pointer hover:bg-purple-50 transition-colors ${
                isToday ? 'bg-purple-50/30' : ''
              }`}
            >
              {dayClasses.map(cls => {
                const teacher = teachers.find(t => t.id === cls.teacherId);
                const sessionKey = `${dateStr}__${cls.id}`;
                const session = sessionsMap[sessionKey] ?? null;
                const studentCount = cls.enrolledStudents?.length ?? 0;
                const markedCount = session
                  ? Object.keys(session.attendance ?? {}).length
                  : 0;

                return (
                  <div
                    key={cls.id}
                    className="mb-1.5 p-2 rounded-lg text-xs border"
                    style={{
                      backgroundColor: (teacher?.color ?? '#7C3AED') + '18',
                      borderColor: (teacher?.color ?? '#7C3AED') + '44',
                    }}
                  >
                    <p className="font-semibold text-gray-800 truncate">
                      {(() => {
                        const iso = getClassSlots(cls)
                          .map(s => scheduledAtForDisplayDay(day, displayTimezone, s.dayOfWeek, s.time))
                          .find(x => x !== null);
                        return iso ? formatISOForDisplay(iso, displayTimezone) : '';
                      })()}
                    </p>
                    {session?.substituteTeacherId ? (
                      <p className="text-orange-600 truncate font-medium">
                        {session.substituteTeacherName}{' '}
                        <span className="text-xs bg-orange-100 px-1 rounded font-semibold">SUB</span>
                      </p>
                    ) : (
                      <p className="text-gray-500 truncate">{teacher?.name ?? cls.teacherName}</p>
                    )}
                    {(cls.sessionsPerWeek ?? 1) > 1 && (
                      <span className="inline-block mt-0.5 px-1 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-600 leading-none">
                        ⚡ {cls.sessionsPerWeek}×/wk
                      </span>
                    )}
                    <p className="text-gray-400 mt-0.5">
                      {session
                        ? `${markedCount}/${studentCount} marked`
                        : `${studentCount} student${studentCount !== 1 ? 's' : ''}`
                      }
                    </p>
                    {session && (
                      <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                        session.status === 'completed'   ? 'bg-green-100 text-green-700' :
                        session.status === 'cancelled'   ? 'bg-gray-100 text-gray-500' :
                        session.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {session.status}
                      </span>
                    )}
                  </div>
                );
              })}
              {/* Makeup sessions — shown even if not a regular class day */}
              {makeupSessions.map(s => {
                const cls = classes.find(c => c.id === s.classId);
                return (
                  <div
                    key={s.id}
                    className="mb-1.5 p-2 rounded-lg text-xs border border-yellow-300 bg-yellow-50"
                  >
                    <p className="font-semibold text-yellow-800 truncate">
                      ↻ {s.scheduledAt ? formatISOForDisplay(s.scheduledAt, displayTimezone) : ''} <span className="font-normal">makeup</span>
                    </p>
                    {s.substituteTeacherId ? (
                      <p className="text-orange-600 truncate font-medium">
                        {s.substituteTeacherName}{' '}
                        <span className="text-xs bg-orange-100 px-1 rounded font-semibold">SUB</span>
                      </p>
                    ) : (
                      <p className="text-yellow-700 truncate">{s.teacherName}</p>
                    )}
                    {cls && (
                      <p className="text-yellow-600 mt-0.5">
                        {Object.keys(s.attendance ?? {}).length}/{cls.enrolledStudents?.length ?? 0} marked
                      </p>
                    )}
                    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                      s.status === 'completed'   ? 'bg-green-100 text-green-700' :
                      s.status === 'cancelled'   ? 'bg-gray-100 text-gray-500' :
                      s.status === 'rescheduled' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {s.status}
                    </span>
                  </div>
                );
              })}
              {dayClasses.length === 0 && makeupSessions.length === 0 && (
                <p className="text-xs text-gray-300 text-center mt-4 italic">—</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Monthly View ──────────────────────────────────────────────────────────────

function MonthlyView({ year, month, classes, sessionsMap, onSelectDay, displayTimezone }) {
  const today = new Date();
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  // Build calendar grid: pad start with nulls
  const startPad = firstDay.getDay();
  const totalCells = startPad + lastDay.getDate();
  const cells = Array.from({ length: Math.ceil(totalCells / 7) * 7 }, (_, i) => {
    const dayNum = i - startPad + 1;
    return (dayNum >= 1 && dayNum <= lastDay.getDate()) ? new Date(year, month, dayNum) : null;
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {DAY_LABELS.map(d => (
          <div key={d} className="p-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          if (!day) {
            return <div key={i} className="p-2 min-h-[80px] border-r border-b border-gray-100 bg-gray-50/50 last:border-r-0" />;
          }

          const isToday = isSameDay(day, today);
          const dateStr = toDateStr(day);

          // Filter classes whose UTC slot falls on this calendar day in displayTimezone
          const dayClasses = classes.filter(
            c => c.status === 'active' && getClassSlots(c).some(s =>
              scheduledAtForDisplayDay(day, displayTimezone, s.dayOfWeek, s.time) !== null
            )
          );

          const daySessionsWithData = dayClasses.map(cls => {
            const key = `${dateStr}__${cls.id}`;
            return sessionsMap[key] ?? null;
          }).filter(Boolean);

          const hasAnySession = daySessionsWithData.length > 0;
          const allDone = hasAnySession && daySessionsWithData.every(s => s.status === 'completed');
          const hasCancelled = daySessionsWithData.some(s => s.status === 'cancelled');

          // Makeup sessions on this date
          const dayMakeups = Object.values(sessionsMap).filter(s =>
            s.isMakeup === true &&
            (s.scheduledAt ? isoToDisplayDate(s.scheduledAt, displayTimezone) : null) === dateStr
          );

          return (
            <div
              key={i}
              onClick={() => onSelectDay(day)}
              className={`p-2 min-h-[80px] border-r border-b border-gray-100 last:border-r-0 cursor-pointer hover:bg-purple-50 transition-colors ${
                isToday ? 'bg-purple-50/40' : ''
              }`}
            >
              <div className={`text-sm font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                isToday ? 'bg-purple-600 text-white' : 'text-gray-700'
              }`}>
                {day.getDate()}
              </div>
              {dayClasses.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    {allDone && <span className="text-green-500 text-xs">✓</span>}
                    {hasCancelled && <span className="text-gray-400 text-xs">✗</span>}
                    <span className="text-xs text-gray-500">
                      {dayClasses.length} class{dayClasses.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                  {/* Colour dots per class */}
                  <div className="flex gap-0.5 flex-wrap">
                    {dayClasses.slice(0, 4).map(cls => {
                      const sessionForDot = sessionsMap[`${dateStr}__${cls.id}`] ?? null;
                      const hasSub = !!sessionForDot?.substituteTeacherId;
                      return (cls.sessionsPerWeek ?? 1) > 1
                        ? <span
                            key={cls.id}
                            className="text-xs leading-none"
                            title={`${cls.sessionsPerWeek}×/wk · ${cls.teacherName}`}
                          >⚡</span>
                        : <span
                            key={cls.id}
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: hasSub ? '#f97316' : '#7C3AED' }}
                            title={hasSub
                              ? `SUB: ${sessionForDot.substituteTeacherName} · ${cls.time}`
                              : `${cls.time} · ${cls.teacherName}`
                            }
                          />;
                    })}
                    {dayClasses.length > 4 && (
                      <span className="text-xs text-gray-400">+{dayClasses.length - 4}</span>
                    )}
                  </div>
                </div>
              )}
              {/* Makeup session indicator */}
              {dayMakeups.length > 0 && (
                <div className="mt-1">
                  {dayMakeups.map(s => (
                    <span
                      key={s.id}
                      className="inline-block text-xs px-1 py-0.5 bg-yellow-100 text-yellow-700 rounded"
                      title={`Makeup: ${s.teacherName} at ${s.scheduledAt ? formatISOForDisplay(s.scheduledAt, displayTimezone) : ''}`}
                    >
                      ↻ makeup
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function AttendanceManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Core data ───────────────────────────────────────────────────────────────
  const [classes, setClasses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [saving, setSaving]     = useState(false);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [teacherFilter, setTeacherFilter] = useState('all');   // teacherId or 'all'
  const [studentSearch, setStudentSearch] = useState('');       // free text
  const [showSuggestions, setShowSuggestions] = useState(false);
  const studentInputRef = useRef(null);

  // ── Display timezone ────────────────────────────────────────────────────────
  // Persisted in localStorage so admins don't have to re-select on every visit.
  const [displayTimezone, setDisplayTimezone] = useState(
    () => localStorage.getItem('attendanceDisplayTimezone') || getBrowserTimezone()
  );
  useEffect(() => {
    localStorage.setItem('attendanceDisplayTimezone', displayTimezone);
  }, [displayTimezone]);

  // ── View state ──────────────────────────────────────────────────────────────
  const [view, setView]           = useState('daily'); // 'daily' | 'weekly' | 'monthly'
  const [selectedDate, setSelectedDate] = useState(new Date());

  // For weekly/monthly nav
  const weekStart = useMemo(() => startOfWeek(selectedDate), [selectedDate]);
  const [monthYear, setMonthYear] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
  });

  // ── Sessions ────────────────────────────────────────────────────────────────
  // dailySessions: { [classId]: sessionDoc }
  // broadSessions: { [dateStr__classId]: sessionDoc } — used for weekly/monthly views
  const [dailySessions, setDailySessions]             = useState({});
  const [dailyMakeupSessions, setDailyMakeupSessions] = useState([]); // makeup sessions on selected date
  const [broadSessions, setBroadSessions]             = useState({});

  // ── Multi-select ─────────────────────────────────────────────────────────────
  // Each key: "classId__childId"
  const [selectedStudents, setSelectedStudents] = useState(new Set());

  // ── Modals ───────────────────────────────────────────────────────────────────
  const [rescheduleModal, setRescheduleModal] = useState(null); // { cls, session, conflictError }
  const [substituteModal, setSubstituteModal] = useState(null); // { cls, session }

  // ── Flash helper ─────────────────────────────────────────────────────────────
  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  // Deduplicated sorted list of all enrolled student names across all classes
  const allStudentNames = useMemo(() => {
    const names = new Set();
    classes.forEach(c =>
      (c.enrolledStudents ?? []).forEach(s => {
        if (s.childName) names.add(s.childName);
      })
    );
    return [...names].sort();
  }, [classes]);

  // ── Filtered classes (in-memory) ─────────────────────────────────────────────
  const filteredClasses = useMemo(() => {
    let result = classes;
    if (teacherFilter !== 'all') {
      result = result.filter(c => c.teacherId === teacherFilter);
    }
    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      result = result.filter(c =>
        (c.enrolledStudents ?? []).some(s =>
          (s.childName ?? '').toLowerCase().includes(q)
        )
      );
    }
    return result;
  }, [classes, teacherFilter, studentSearch]);

  // ── Data loading ─────────────────────────────────────────────────────────────

  const fetchBaseData = async () => {
    try {
      setLoading(true);
      const [classSnap, teacherSnap] = await Promise.all([
        getDocs(query(collection(db, 'classes'))),  // filter active + sort client-side to avoid composite index
        getDocs(query(collection(db, 'teachers'), orderBy('name'))),
      ]);
      const allClasses = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter active and sort by dayOfWeek client-side (no composite index needed)
      const DAY_ORDER = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
      setClasses(
        allClasses
          .filter(c => c.status === 'active')
          .sort((a, b) => DAY_ORDER.indexOf(getClassSlots(a)[0]?.dayOfWeek ?? a.dayOfWeek) - DAY_ORDER.indexOf(getClassSlots(b)[0]?.dayOfWeek ?? b.dayOfWeek))
      );
      setTeachers(teacherSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('AttendanceManager base load error:', err);
      setError('Failed to load class data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  // Load sessions for the currently selected single day.
  // Uses a UTC range query bracketing the calendar day in displayTimezone.
  const loadDailySessions = useCallback(async (date, tz) => {
    try {
      const { startISO, endISO } = utcRangeForCalendarDay(date, tz);
      const snap = await getDocs(
        query(
          collection(db, 'sessions'),
          where('scheduledAt', '>=', startISO),
          where('scheduledAt', '<=', endISO)
        )
      );
      const regularMap = {};  // { [classId]: session } — one per class slot
      const makeupList = [];  // makeup sessions that may not match today's day-of-week
      snap.docs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (data.isMakeup) {
          makeupList.push(data);
        } else {
          regularMap[data.classId] = data;
        }
      });
      setDailySessions(regularMap);
      setDailyMakeupSessions(makeupList);
    } catch (err) {
      console.error('Load daily sessions error:', err);
    }
  }, []);

  // Load sessions for a date range (weekly / monthly).
  // startStr/endStr are YYYY-MM-DD calendar boundaries; converted to UTC ISO via tz.
  // Sessions are keyed by their display-timezone date so weekly/monthly cells match.
  const loadRangeSessions = useCallback(async (startStr, endStr, tz) => {
    try {
      // Compute the UTC range that encompasses the full calendar span in the viewer's tz
      const { startISO } = utcRangeForCalendarDay(new Date(startStr + 'T12:00:00'), tz);
      const { endISO   } = utcRangeForCalendarDay(new Date(endStr   + 'T12:00:00'), tz);

      const snap = await getDocs(
        query(
          collection(db, 'sessions'),
          where('scheduledAt', '>=', startISO),
          where('scheduledAt', '<=', endISO),
          orderBy('scheduledAt')
        )
      );
      const map = {};
      snap.docs.forEach(d => {
        const data = { id: d.id, ...d.data() };
        // Derive display-timezone date for the broadSessions key so calendar cells match
        const displayDate = data.scheduledAt ? isoToDisplayDate(data.scheduledAt, tz) : null;
        // Makeup sessions use sessionId as key to avoid colliding with regular sessions
        // that share the same classId on the same date
        const key = data.isMakeup
          ? `makeup__${data.id}`
          : `${displayDate}__${data.classId}`;
        map[key] = data;
      });
      setBroadSessions(map);
    } catch (err) {
      console.error('Load range sessions error:', err);
    }
  }, []);

  useEffect(() => { fetchBaseData(); }, []);

  // Reload sessions whenever date, view, or displayTimezone changes
  useEffect(() => {
    if (view === 'daily') {
      loadDailySessions(selectedDate, displayTimezone);
      setSelectedStudents(new Set()); // clear cross-class selection on date change
    } else if (view === 'weekly') {
      const endOfWeek = addDays(weekStart, 6);
      loadRangeSessions(toDateStr(weekStart), toDateStr(endOfWeek), displayTimezone);
    } else if (view === 'monthly') {
      const start = new Date(monthYear.year, monthYear.month, 1);
      const end   = new Date(monthYear.year, monthYear.month + 1, 0);
      loadRangeSessions(toDateStr(start), toDateStr(end), displayTimezone);
    }
  }, [view, selectedDate, weekStart, monthYear, displayTimezone, loadDailySessions, loadRangeSessions]);

  // ── Session upsert ───────────────────────────────────────────────────────────
  // Gets or creates the session document for a class on the current day.
  // Returns the session document (with id).
  //
  // FIX: Two-stage guard against duplicate session creation.
  //   Stage 1 — fast path: return from React state if already loaded (common case,
  //             no extra Firestore reads).
  //   Stage 2 — safety check: if state is empty for this class, query Firestore
  //             before creating. Prevents duplicates when state is stale after a
  //             view toggle (daily→weekly→daily) or date navigation, which causes
  //             loadDailySessions to fully replace dailySessions via
  //             setDailySessions(regularMap) — potentially dropping a session that
  //             was created earlier in the same admin session.

  async function getOrCreateSession(cls) {
    // Stage 1 — fast path: trust warm state
    const stateSession = dailySessions[cls.id];
    if (stateSession) return stateSession;

    // Stage 2 — safety check: query Firestore before creating to prevent duplicates
    const { startISO, endISO } = utcRangeForCalendarDay(selectedDate, displayTimezone);
    const existingSnap = await getDocs(
      query(
        collection(db, 'sessions'),
        where('classId', '==', cls.id),
        where('scheduledAt', '>=', startISO),
        where('scheduledAt', '<=', endISO)
      )
    );
    const regularDoc = existingSnap.docs.find(d => !d.data().isMakeup);
    if (regularDoc) {
      const existing = { id: regularDoc.id, ...regularDoc.data() };
      setDailySessions(m => ({ ...m, [cls.id]: existing }));
      return existing;
    }

    // Nothing in state or Firestore — safe to create
    // Find which slot of this class falls on the currently selected display day
    const matchingSlot = getClassSlots(cls).find(s =>
      scheduledAtForDisplayDay(selectedDate, displayTimezone, s.dayOfWeek, s.time) !== null
    ) ?? getClassSlots(cls)[0];

    const scheduledAt = matchingSlot
      ? (scheduledAtForDisplayDay(selectedDate, displayTimezone, matchingSlot.dayOfWeek, matchingSlot.time)
          ?? new Date().toISOString())
      : new Date().toISOString();

    const now = new Date().toISOString();
    const newSession = {
      classId:         cls.id,
      teacherId:       cls.teacherId,
      teacherName:     cls.teacherName,
      scheduledAt,
      duration:        cls.duration ?? 60,
      status:          'scheduled',
      attendance:      {},
      rescheduledFrom: null,
      jitsiRoomUrl:    null,
      notes:           '',
      createdAt:       now,
      updatedAt:       now,
    };

    const docRef = await addDoc(collection(db, 'sessions'), newSession);
    const created = { id: docRef.id, ...newSession };

    // Update local map immediately
    setDailySessions(m => ({ ...m, [cls.id]: created }));
    return created;
  }

  // ── Batch increment sessionsCompleted for multiple students ──────────────────
  // Replaces the old single-student incrementSessionsCompleted.
  // By updating ALL students in ONE write, we eliminate the race condition that
  // occurred when Promise.all fired N concurrent writes to the same Firestore
  // document — where only the last write won and other counts were lost.

  async function batchIncrementSessionsCompleted(classId, studentsToIncrement, sessionScheduledAt) {
    // studentsToIncrement: [{ childId, userId }, ...]
    if (studentsToIncrement.length === 0) return;

    const cls = classes.find(c => c.id === classId);
    if (!cls) return;

    const incrementMap = new Map(studentsToIncrement.map(s => [s.childId, s.userId]));

    const updatedStudents = (cls.enrolledStudents ?? []).map(s => {
      if (!incrementMap.has(s.childId) || incrementMap.get(s.childId) !== s.userId) return s;

      const newCount = (s.sessionsCompleted ?? 0) + 1;
      const total    = s.sessionsTotal ?? 6;
      const updated  = { ...s, sessionsCompleted: newCount };

      // First countable session ever for this child in this class
      if (!s.firstSessionAt && newCount === 1) {
        updated.firstSessionAt = sessionScheduledAt;
      }

      // This mark completes the course — record the actual last session ISO timestamp
      if (newCount >= total && !s.lastSessionAt) {
        updated.lastSessionAt = sessionScheduledAt;
      }

      return updated;
    });

    await updateDoc(doc(db, 'classes', classId), { enrolledStudents: updatedStudents });

    // Update local classes state
    setClasses(prev =>
      prev.map(c => c.id === classId ? { ...c, enrolledStudents: updatedStudents } : c)
    );
  }

  // ── Batch decrement sessionsCompleted for multiple students ──────────────────
  // Used when clearing attendance that was previously counted (present/absent).

  async function batchDecrementSessionsCompleted(classId, childIds) {
    // childIds: [childId string, ...]
    if (childIds.length === 0) return;

    // Read the class document FRESH from Firestore instead of trusting local
    // `classes` React state. Stale state caused dropped decrements: deleting two
    // sessions in quick succession let both writes begin from the same in-memory
    // snapshot, so the full-array overwrite clobbered one decrement and
    // sessionsCompleted drifted above the real session count.
    const classRef = doc(db, 'classes', classId);
    const classSnap = await getDoc(classRef);
    if (!classSnap.exists()) return;
    const enrolledStudents = classSnap.data().enrolledStudents ?? [];

    const decrementSet = new Set(childIds);

    const updatedStudents = enrolledStudents.map(s => {
      if (!decrementSet.has(s.childId)) return s;

      const newCount = Math.max(0, (s.sessionsCompleted ?? 0) - 1);
      const total    = s.sessionsTotal ?? 6;
      const updated  = { ...s, sessionsCompleted: newCount };

      // Clear lastSessionAt if count drops back below total
      if (s.lastSessionAt && newCount < total) {
        updated.lastSessionAt = null;
      }

      // Clear firstSessionAt if count goes back to 0
      if (s.firstSessionAt && newCount === 0) {
        updated.firstSessionAt = null;
      }

      return updated;
    });

    await updateDoc(doc(db, 'classes', classId), { enrolledStudents: updatedStudents });

    setClasses(prev =>
      prev.map(c => c.id === classId ? { ...c, enrolledStudents: updatedStudents } : c)
    );
  }

  // ── Mark attendance for a list of selKeys ───────────────────────────────────
  // selKeys: ["classId__childId", ...]
  // requestedValue: 'present' | 'absent' | 'rescheduled'
  //
  // FIX: now correctly handles mid-session value changes:
  //   countable → non-countable (present/absent → rescheduled): decrements count
  //   non-countable → countable (rescheduled → present/absent): increments count
  //   countable → countable (present ↔ absent): no change to count

  const handleMarkStudents = async (selKeys, requestedValue) => {
    if (selKeys.length === 0) return;
    setSaving(true);

    try {
      // Group by classId
      const byClass = {};
      selKeys.forEach(key => {
        const [classId, childId] = key.split('__');
        if (!byClass[classId]) byClass[classId] = [];
        byClass[classId].push(childId);
      });

      for (const [classId, childIds] of Object.entries(byClass)) {
        const cls = classes.find(c => c.id === classId);
        if (!cls) continue;

        const session = await getOrCreateSession(cls);
        const prevAttendance = { ...session.attendance };
        const newAttendance  = { ...prevAttendance };

        // Collect all students that need incrementing or decrementing
        const studentsToIncrement = [];
        const studentsToDec = [];

        childIds.forEach(childId => {
          const student = (cls.enrolledStudents ?? []).find(s => s.childId === childId);
          if (!student) return;

          const actualValue = resolveAttendanceValue(student, requestedValue);
          const prevValue   = prevAttendance[childId] ?? null;

          newAttendance[childId] = actualValue;

          const prevCounted = prevValue !== null && shouldCountSession(prevValue);
          const newCounted  = shouldCountSession(actualValue);

          if (!prevCounted && newCounted) {
            // unmarked→countable OR rescheduled→countable: increment
            studentsToIncrement.push({ childId, userId: student.userId });
          } else if (prevCounted && !newCounted) {
            // countable→rescheduled: decrement
            studentsToDec.push(childId);
          }
          // countable→countable (present↔absent): no change to count
        });

        const now = new Date().toISOString();
        const updatedSession = {
          ...session,
          attendance: newAttendance,
          status: 'completed',
          updatedAt: now,
        };

        await updateDoc(doc(db, 'sessions', session.id), {
          attendance: newAttendance,
          status: 'completed',
          updatedAt: now,
        });

        // Single batched write — eliminates the race condition from concurrent writes
        if (studentsToIncrement.length > 0) {
          await batchIncrementSessionsCompleted(classId, studentsToIncrement, session.scheduledAt);
        }
        if (studentsToDec.length > 0) {
          await batchDecrementSessionsCompleted(classId, studentsToDec);
        }

        // Update local session map
        setDailySessions(m => ({ ...m, [classId]: updatedSession }));
      }

      setSelectedStudents(new Set());
      showSuccess('Attendance saved.');
    } catch (err) {
      console.error('Mark attendance error:', err);
      setError('Failed to save attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Mark all students in a class as present ─────────────────────────────────

  const handleMarkAllPresent = async (cls) => {
    const students = cls.enrolledStudents ?? [];
    if (students.length === 0) return;
    const keys = students.map(s => `${cls.id}__${s.childId}`);
    await handleMarkStudents(keys, 'present');
  };

  // ── Clear attendance for a single student ───────────────────────────────────
  // Removes the student's mark from the session, decrements sessionsCompleted
  // if the previous value was counted (present/absent), and reverts the session
  // status to 'scheduled' if the attendance object is now empty.
  // Works for both regular and makeup sessions (isMakeup flag controls which
  // local state map is updated).

  const handleClearStudentAttendance = async (session, cls, student, isMakeup = false) => {
    if (!session) return;

    const prevValue = session.attendance?.[student.childId];
    if (!prevValue) return; // Nothing to clear

    setSaving(true);
    try {
      const newAttendance = { ...session.attendance };
      delete newAttendance[student.childId];

      const now = new Date().toISOString();
      const allClear = Object.keys(newAttendance).length === 0;

      // Decrement sessionsCompleted if the cleared value was counted
      if (shouldCountSession(prevValue)) {
        await batchDecrementSessionsCompleted(cls.id, [student.childId]);
      }

      if (allClear) {
        // Last mark cleared — delete the session doc entirely so no pill appears,
        // matching the original state before any attendance was taken.
        await deleteDoc(doc(db, 'sessions', session.id));

        if (isMakeup) {
          setDailyMakeupSessions(prev => prev.filter(s => s.id !== session.id));
        } else {
          setDailySessions(m => {
            const next = { ...m };
            delete next[cls.id];
            return next;
          });
        }
      } else {
        // Still some marks remaining — update the doc
        await updateDoc(doc(db, 'sessions', session.id), {
          attendance: newAttendance,
          status: session.status,
          updatedAt: now,
        });

        const updatedSession = { ...session, attendance: newAttendance, updatedAt: now };

        if (isMakeup) {
          setDailyMakeupSessions(prev =>
            prev.map(s => s.id === session.id ? updatedSession : s)
          );
        } else {
          setDailySessions(m => ({ ...m, [cls.id]: updatedSession }));
        }
      }

      showSuccess('Attendance cleared.');
    } catch (err) {
      console.error('Clear attendance error:', err);
      setError('Failed to clear attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Reset all attendance for a class session ─────────────────────────────────
  // Clears every student's mark, decrements counts for those who were counted,
  // and reverts the session status to 'scheduled'.

  const handleResetAllAttendance = async (cls, session) => {
    if (!session) return;

    const attendance = session.attendance ?? {};
    const markedStudents = Object.keys(attendance);
    if (markedStudents.length === 0) return;

    if (!window.confirm(
      `Reset all attendance for this session?\n\n` +
      `This will clear all markings and subtract a session count from any student who was marked Present or Absent.`
    )) return;

    setSaving(true);
    try {
      // Decrement all students whose previous values were counted
      const studentsToDec = markedStudents.filter(childId =>
        shouldCountSession(attendance[childId])
      );

      if (studentsToDec.length > 0) {
        await batchDecrementSessionsCompleted(cls.id, studentsToDec);
      }

      // Delete the session document entirely — restores the true "nothing happened"
      // state. Before any attendance is taken no session doc exists, so deleting
      // here means the class card shows no pill, matching that original state.
      await deleteDoc(doc(db, 'sessions', session.id));

      // Remove from local daily map so the pill disappears immediately
      setDailySessions(m => {
        const next = { ...m };
        delete next[cls.id];
        return next;
      });

      showSuccess('Attendance reset. All marks cleared.');
    } catch (err) {
      console.error('Reset attendance error:', err);
      setError('Failed to reset attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Mark attendance for a makeup session ────────────────────────────────────
  // Writes directly to the makeup session doc by its Firestore ID.
  // makeupSession: the session doc (must have .id and .classId)
  // childIds: array of childId strings to mark
  // requestedValue: 'present' | 'absent' | 'rescheduled'
  //
  // FIX: now correctly handles mid-session value changes, same as handleMarkStudents.

  const handleMarkMakeupAttendance = async (makeupSession, childIds, requestedValue) => {
    if (!childIds || childIds.length === 0) return;
    setSaving(true);
    try {
      const cls = classes.find(c => c.id === makeupSession.classId);
      const prevAttendance = { ...makeupSession.attendance };
      const newAttendance  = { ...prevAttendance };

      // Collect students to increment or decrement
      const studentsToIncrement = [];
      const studentsToDec = [];

      childIds.forEach(childId => {
        const student = (cls?.enrolledStudents ?? []).find(s => s.childId === childId);
        if (!student) return;

        const actualValue = resolveAttendanceValue(student, requestedValue);
        const prevValue   = prevAttendance[childId] ?? null;

        newAttendance[childId] = actualValue;

        const prevCounted = prevValue !== null && shouldCountSession(prevValue);
        const newCounted  = shouldCountSession(actualValue);

        if (!prevCounted && newCounted) {
          studentsToIncrement.push({ childId, userId: student.userId });
        } else if (prevCounted && !newCounted) {
          studentsToDec.push(childId);
        }
      });

      const now = new Date().toISOString();
      await updateDoc(doc(db, 'sessions', makeupSession.id), {
        attendance: newAttendance,
        status: 'completed',
        updatedAt: now,
      });

      // Single batched write — same race condition fix as handleMarkStudents
      if (studentsToIncrement.length > 0) {
        await batchIncrementSessionsCompleted(makeupSession.classId, studentsToIncrement, makeupSession.scheduledAt);
      }
      if (studentsToDec.length > 0) {
        await batchDecrementSessionsCompleted(makeupSession.classId, studentsToDec);
      }

      // Update local makeup sessions list
      setDailyMakeupSessions(prev =>
        prev.map(s =>
          s.id === makeupSession.id
            ? { ...s, attendance: newAttendance, status: 'completed', updatedAt: now }
            : s
        )
      );

      showSuccess('Makeup attendance saved.');
    } catch (err) {
      console.error('Mark makeup attendance error:', err);
      setError('Failed to save makeup attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Assign / remove substitute teacher for a session ────────────────────────
  // substituteTeacherId / substituteTeacherName are null to remove the substitute.
  // Works for both regular and makeup sessions — identified via session.isMakeup.

  const handleAssignSubstitute = async (cls, session, substituteTeacherId, substituteTeacherName) => {
    // If no session yet and we're clearing — nothing to do
    if (!session && !substituteTeacherId) return;
    setSaving(true);
    try {
      const sessionDoc = session ?? await getOrCreateSession(cls);
      const now = new Date().toISOString();

      const updatePayload = substituteTeacherId
        ? { substituteTeacherId, substituteTeacherName, updatedAt: now }
        : { substituteTeacherId: null, substituteTeacherName: null, updatedAt: now };

      await updateDoc(doc(db, 'sessions', sessionDoc.id), updatePayload);

      const updated = { ...sessionDoc, ...updatePayload };

      if (sessionDoc.isMakeup) {
        setDailyMakeupSessions(prev =>
          prev.map(s => s.id === sessionDoc.id ? updated : s)
        );
      } else {
        setDailySessions(m => ({ ...m, [cls.id]: updated }));
      }

      showSuccess(
        substituteTeacherId
          ? `Substitute assigned: ${substituteTeacherName}`
          : 'Substitute removed.'
      );
    } catch (err) {
      console.error('Assign substitute error:', err);
      setError('Failed to update substitute. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel class ─────────────────────────────────────────────────────────────
  //
  // FIX 1: Decrements sessionsCompleted for any student already marked with a
  //         countable value (present/absent) before the cancel is applied.
  // FIX 2: Always writes the cancelled session into broadSessions (removed the
  //         !m[key] guard that silently skipped newly created sessions, causing
  //         the cancelled pill to not appear in the weekly/monthly view).

  const handleCancelClass = async (cls, existingSession) => {
    if (!window.confirm(
      `Cancel this session? This records that the teacher cancelled class. No attendance will be recorded and this session will not count toward any student's total. The course effectively runs one extra week.`
    )) return;

    setSaving(true);
    try {
      const session = existingSession ?? await getOrCreateSession(cls);
      const now = new Date().toISOString();

      // Always read fresh from Firestore before decrementing — do not trust the
      // session object passed in from local state, which may be stale if the admin
      // marked attendance moments before clicking Cancel.
      const freshSnap = await getDoc(doc(db, 'sessions', session.id));
      const freshAttendance = freshSnap.exists() ? (freshSnap.data().attendance ?? {}) : {};
      const studentsToDec = Object.keys(freshAttendance).filter(childId =>
        shouldCountSession(freshAttendance[childId])
      );

      if (studentsToDec.length > 0) {
        await batchDecrementSessionsCompleted(cls.id, studentsToDec);
      }

      await updateDoc(doc(db, 'sessions', session.id), {
        attendance: {},
        status: 'cancelled',
        updatedAt: now,
      });

      const cancelledSession = { ...session, attendance: {}, status: 'cancelled', updatedAt: now };

      setDailySessions(m => ({ ...m, [cls.id]: cancelledSession }));

      // Always update broadSessions so the cancelled pill appears immediately in
      // weekly/monthly view regardless of whether a session doc existed before.
      setBroadSessions(m => ({
        ...m,
        [`${isoToDisplayDate(session.scheduledAt, displayTimezone)}__${session.classId}`]: cancelledSession,
      }));

      showSuccess('Class cancelled. Session will not count toward students\' totals.');
    } catch (err) {
      console.error('Cancel class error:', err);
      setError('Failed to cancel class.');
    } finally {
      setSaving(false);
    }
  };

  // ── Reschedule class ─────────────────────────────────────────────────────────
  // Helper: convert a local date + time in a given timezone to a UTC ISO string.
  function localDateTimeToScheduledAt(dateStr, timeStr, timezone) {
    const localDayName = DAYS_OF_WEEK[new Date(dateStr + 'T12:00:00').getDay()];
    const { utcTime, utcDay } = timeToUTC(timeStr, localDayName, timezone);
    const localDayIdx = DAYS_OF_WEEK.indexOf(localDayName);
    const utcDayIdx   = DAYS_OF_WEEK.indexOf(utcDay);
    let dayDiff = utcDayIdx - localDayIdx;
    if (dayDiff > 3)  dayDiff -= 7;
    if (dayDiff < -3) dayDiff += 7;
    const utcDateMs  = new Date(dateStr + 'T12:00:00').getTime() + dayDiff * 86400000;
    const utcDateStr = new Date(utcDateMs).toISOString().slice(0, 10);
    return `${utcDateStr}T${utcTime}:00.000Z`;
  }

  const handleRescheduleClass = async (newDateStr, newTimeStr, entryTimezone) => {
    const { cls, session: existingSession } = rescheduleModal;
    setSaving(true);
    try {
      // Convert the new date+time from entryTimezone → UTC for comparison
      const makeupScheduledAt = localDateTimeToScheduledAt(newDateStr, newTimeStr, entryTimezone);
      const makeupUTCDate = new Date(makeupScheduledAt);
      const makeupUTCDay  = DAYS_OF_WEEK[makeupUTCDate.getUTCDay()];
      const makeupUTCMins = makeupUTCDate.getUTCHours() * 60 + makeupUTCDate.getUTCMinutes();
      const newEnd        = makeupUTCMins + (cls.duration ?? 60);

      // ── Teacher conflict check ────────────────────────────────────────────────
      // 1. Check recurring classes: does the teacher have a slot on the same UTC weekday
      //    with overlapping time?
      const recurringConflict = classes.find(other => {
        if (other.id === cls.id)               return false;
        if (other.teacherId !== cls.teacherId) return false;
        if (other.status === 'suspended')      return false;
        return getClassSlots(other).some(slot => {
          if (slot.dayOfWeek !== makeupUTCDay) return false;
          const [oh, om] = slot.time.split(':').map(Number);
          const oStart = oh * 60 + om;
          const oEnd   = oStart + (other.duration ?? 60);
          return makeupUTCMins < oEnd && oStart < newEnd;
        });
      });

      if (recurringConflict) {
        const conflictSlot = getClassSlots(recurringConflict).find(s => s.dayOfWeek === makeupUTCDay);
        setRescheduleModal(m => ({ ...m, conflictError:
          `${cls.teacherName} already has a recurring class on ${makeupUTCDay.charAt(0).toUpperCase() + makeupUTCDay.slice(1)} (UTC) at ${formatTimeDisplay(conflictSlot?.time ?? recurringConflict.time, entryTimezone)}. Choose a different time.`
        }));
        setSaving(false);
        return;
      }

      // 2. Check existing sessions on that UTC day for the same teacher
      const { startISO: cfStart, endISO: cfEnd } = utcRangeForCalendarDay(
        new Date(newDateStr + 'T12:00:00'), entryTimezone
      );
      const makeupSnap = await getDocs(
        query(
          collection(db, 'sessions'),
          where('scheduledAt', '>=', cfStart),
          where('scheduledAt', '<=', cfEnd)
        )
      );
      const makeupConflict = makeupSnap.docs.find(d => {
        const s = d.data();
        if (d.id === existingSession?.id)  return false; // skip self
        if (s.teacherId !== cls.teacherId) return false; // different teacher
        const sd = new Date(s.scheduledAt);
        const sStart = sd.getUTCHours() * 60 + sd.getUTCMinutes();
        const sEnd   = sStart + (s.duration ?? 60);
        return makeupUTCMins < sEnd && sStart < newEnd;
      });

      if (makeupConflict) {
        const mc = makeupConflict.data();
        setRescheduleModal(m => ({ ...m, conflictError:
          `${cls.teacherName} already has a session on ${newDateStr} at ${formatISOForDisplay(mc.scheduledAt, entryTimezone)} (${entryTimezone}). Choose a different time.`
        }));
        setSaving(false);
        return;
      }
      // ── End conflict check ───────────────────────────────────────────────────

      const session = existingSession ?? await getOrCreateSession(cls);
      const students = cls.enrolledStudents ?? [];
      const newAttendance = {};
      students.forEach(s => { newAttendance[s.childId] = 'rescheduled'; });

      const now = new Date().toISOString();

      // Update current session to rescheduled
      await updateDoc(doc(db, 'sessions', session.id), {
        attendance: newAttendance,
        status: 'rescheduled',
        updatedAt: now,
      });

      // Create new makeup session with UTC scheduledAt
      await addDoc(collection(db, 'sessions'), {
        classId:         cls.id,
        teacherId:       cls.teacherId,
        teacherName:     cls.teacherName,
        scheduledAt:     makeupScheduledAt,
        duration:        cls.duration ?? 60,
        status:          'scheduled',
        attendance:      {},
        isMakeup:        true,               // key flag — not a recurring slot
        rescheduledFrom: session.id,
        jitsiRoomUrl:    null,
        notes:           '',
        createdAt:       now,
        updatedAt:       now,
      });

      // Reload range sessions so weekly/monthly view shows the new makeup session
      if (view === 'weekly') {
        const endOfWeek = addDays(weekStart, 6);
        await loadRangeSessions(toDateStr(weekStart), toDateStr(endOfWeek));
      } else if (view === 'monthly') {
        const start = new Date(monthYear.year, monthYear.month, 1);
        const end   = new Date(monthYear.year, monthYear.month + 1, 0);
        await loadRangeSessions(toDateStr(start), toDateStr(end));
      }

      // Update local state — makeup sessions live in a different map than regular sessions
      const updatedSession = { ...session, attendance: newAttendance, status: 'rescheduled', updatedAt: now };
      if (session.isMakeup) {
        // Update the makeup list — replace the matching entry by id
        setDailyMakeupSessions(prev =>
          prev.map(s => s.id === session.id ? updatedSession : s)
        );
        // Also remove from broadSessions since it's now rescheduled
        setBroadSessions(m => {
          const next = { ...m };
          Object.keys(next).forEach(k => {
            if (next[k]?.id === session.id) next[k] = updatedSession;
          });
          return next;
        });
      } else {
        // Regular session — update by classId as before
        setDailySessions(m => ({ ...m, [cls.id]: updatedSession }));
        setBroadSessions(m => {
          const key = session.scheduledAt
            ? `${isoToDisplayDate(session.scheduledAt, displayTimezone)}__${session.classId}`
            : null;
          if (!key || !m[key]) return m;
          return { ...m, [key]: updatedSession };
        });
      }

      setRescheduleModal(null);
      showSuccess(`Class rescheduled. Makeup session created for ${newDateStr} at ${newTimeStr} (${entryTimezone}).`);
    } catch (err) {
      console.error('Reschedule error:', err);
      setError('Failed to reschedule class.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete session ───────────────────────────────────────────────────────────
  // Hard deletes the session document and rolls back sessionsCompleted for any
  // students who were marked with a countable value (present/absent).
  // Always reads fresh from Firestore before decrementing — never trusts local
  // React state, which may be stale if attendance was marked moments before delete.

  const handleDeleteSession = async (cls, session) => {
    if (!window.confirm(
      `Delete this session record for ${cls.teacherName} on ${session.scheduledAt ? isoToDisplayDate(session.scheduledAt, displayTimezone) : ''}?\n\n` +
      `This cannot be undone. If attendance was already saved, students' session counts will be rolled back automatically.`
    )) return;

    try {
      // Always read fresh from Firestore — do not trust local state for the
      // decrement. React state may not have flushed the latest attendance if the
      // admin clicks Delete immediately after marking.
      const freshSnap = await getDoc(doc(db, 'sessions', session.id));
      const freshAttendance = freshSnap.exists() ? (freshSnap.data().attendance ?? {}) : {};
      const studentsToDec = Object.keys(freshAttendance).filter(childId =>
        shouldCountSession(freshAttendance[childId])
      );
      if (studentsToDec.length > 0) {
        await batchDecrementSessionsCompleted(cls.id, studentsToDec);
      }

      await deleteDoc(doc(db, 'sessions', session.id));

      // Remove from daily map
      setDailySessions(m => {
        const next = { ...m };
        delete next[cls.id];
        return next;
      });

      // Remove from daily makeup list
      setDailyMakeupSessions(prev => prev.filter(s => s.id !== session.id));

      // Remove from broad (weekly/monthly) map — key could be either format
      setBroadSessions(m => {
        const next = { ...m };
        // Regular key (derived from UTC scheduledAt → display date)
        if (session.scheduledAt) {
          delete next[`${isoToDisplayDate(session.scheduledAt, displayTimezone)}__${session.classId}`];
        }
        // Also remove by sessionId in case it was stored differently
        Object.keys(next).forEach(k => {
          if (next[k]?.id === session.id) delete next[k];
        });
        return next;
      });

      showSuccess('Session deleted.');
    } catch (err) {
      console.error('Delete session error:', err);
      setError('Failed to delete session.');
    }
  };

  // ── Selection helpers ────────────────────────────────────────────────────────

  function handleToggleStudent(selKey) {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      next.has(selKey) ? next.delete(selKey) : next.add(selKey);
      return next;
    });
  }

  function handleSelectAllInClass(classId, students) {
    const keys = students.map(s => `${classId}__${s.childId}`);
    const allSelected = keys.every(k => selectedStudents.has(k));
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  }

  // ── Date/period navigation ───────────────────────────────────────────────────

  function navigateDay(delta) {
    setSelectedDate(d => addDays(d, delta));
  }

  function navigateWeek(delta) {
    setSelectedDate(d => addDays(d, delta * 7));
  }

  function navigateMonth(delta) {
    setMonthYear(({ year, month }) => {
      let m = month + delta;
      let y = year;
      if (m > 11) { m = 0; y++; }
      if (m < 0)  { m = 11; y--; }
      return { year: y, month: m };
    });
  }

  function handleSelectDay(day) {
    setSelectedDate(day);
    setView('daily');
  }

  // ── Timezone change handler ──────────────────────────────────────────────────
  // When the display timezone changes in daily view, nudge selectedDate ±1 day
  // if the current date has no classes in the new timezone.
  // This handles midnight-crossing cases (e.g. Monday Pacific → Tuesday Cairo).
  function handleDisplayTimezoneChange(newTz) {
    setDisplayTimezone(newTz);
    if (view !== 'daily') return;

    const hasClassOnDate = (date) =>
      classes.some(c =>
        c.status === 'active' &&
        getClassSlots(c).some(s =>
          scheduledAtForDisplayDay(date, newTz, s.dayOfWeek, s.time) !== null
        )
      );

    if (hasClassOnDate(selectedDate)) return;

    const tomorrow  = addDays(selectedDate, 1);
    const yesterday = addDays(selectedDate, -1);

    if (hasClassOnDate(tomorrow)) {
      setSelectedDate(tomorrow);
    } else if (hasClassOnDate(yesterday)) {
      setSelectedDate(yesterday);
    }
  }

  // ── Stats for header ─────────────────────────────────────────────────────────

  const todayClassCount = filteredClasses.filter(
    c => c.status === 'active' && getClassSlots(c).some(s =>
      scheduledAtForDisplayDay(selectedDate, displayTimezone, s.dayOfWeek, s.time) !== null
    )
  ).length;
  const todayStudentCount = filteredClasses
    .filter(c => c.status === 'active' && getClassSlots(c).some(s =>
      scheduledAtForDisplayDay(selectedDate, displayTimezone, s.dayOfWeek, s.time) !== null
    ))
    .reduce((n, c) => n + (c.enrolledStudents?.length ?? 0), 0);
  const markedTodayCount = Object.values(dailySessions)
    .reduce((n, s) => n + Object.keys(s.attendance ?? {}).length, 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
              {view === 'daily' && (
                <p className="text-sm text-gray-500">
                  {todayClassCount} class{todayClassCount !== 1 ? 'es' : ''} ·{' '}
                  {todayStudentCount} student{todayStudentCount !== 1 ? 's' : ''} ·{' '}
                  {markedTodayCount} marked
                </p>
              )}
            </div>
          </div>

          {/* View toggle + timezone selector */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Teacher filter */}
            <select
              value={teacherFilter}
              onChange={e => setTeacherFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              title="Filter by teacher"
            >
              <option value="all">All Teachers</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Student autocomplete search */}
            <div className="relative">
              <input
                ref={studentInputRef}
                type="text"
                value={studentSearch}
                onChange={e => { setStudentSearch(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="Search student…"
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 w-40 pr-6"
              />
              {studentSearch && (
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setStudentSearch(''); setShowSuggestions(false); studentInputRef.current?.focus(); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear"
                >
                  <X size={12} />
                </button>
              )}
              {showSuggestions && studentSearch.trim() && (() => {
                const q = studentSearch.trim().toLowerCase();
                const matches = allStudentNames.filter(n => n.toLowerCase().includes(q));
                if (matches.length === 0) return null;
                return (
                  <div className="absolute left-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                    {matches.map(name => (
                      <button
                        key={name}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setStudentSearch(name); setShowSuggestions(false); }}
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            <select
              value={displayTimezone}
              onChange={e => handleDisplayTimezoneChange(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              title="Display timezone for all times"
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <div className="flex bg-gray-200 p-1 rounded-xl gap-1">
              {['daily', 'weekly', 'monthly'].map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                    view === v
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} /> {successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} /> {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* ── DAILY VIEW ── */}
        {view === 'daily' && (
          <>
            {/* Date navigation */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigateDay(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex-1 text-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  {formatDisplayDate(selectedDate, displayTimezone)}
                </h2>
                {!isSameDay(selectedDate, new Date()) && (
                  <button
                    onClick={() => setSelectedDate(new Date())}
                    className="text-xs text-purple-600 hover:underline mt-0.5"
                  >
                    Back to today
                  </button>
                )}
              </div>
              <button
                onClick={() => navigateDay(1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <DailyView
              date={selectedDate}
              classes={filteredClasses}
              teachers={teachers}
              sessionsMap={dailySessions}
              makeupSessions={dailyMakeupSessions}
              selectedStudents={selectedStudents}
              onToggleStudent={handleToggleStudent}
              onMarkStudent={handleMarkStudents}
              onMarkAllPresent={handleMarkAllPresent}
              onCancelClass={handleCancelClass}
              onRescheduleClass={(cls, session) => setRescheduleModal({ cls, session })}
              onSelectAllInClass={handleSelectAllInClass}
              onClearSelection={() => setSelectedStudents(new Set())}
              onDeleteSession={handleDeleteSession}
              onMarkMakeupAttendance={handleMarkMakeupAttendance}
              onAssignSubstitute={(cls, session) => setSubstituteModal({ cls, session })}
              onClearStudentAttendance={handleClearStudentAttendance}
              onResetAllAttendance={handleResetAllAttendance}
              displayTimezone={displayTimezone}
            />
          </>
        )}

        {/* ── WEEKLY VIEW ── */}
        {view === 'weekly' && (
          <>
            {/* Week navigation */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigateWeek(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex-1 text-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  Week of {weekStart.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Click any day to open attendance</p>
              </div>
              <button
                onClick={() => navigateWeek(1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <WeeklyView
              weekStart={weekStart}
              classes={filteredClasses}
              sessionsMap={broadSessions}
              teachers={teachers}
              onSelectDay={handleSelectDay}
              displayTimezone={displayTimezone}
            />
          </>
        )}

        {/* ── MONTHLY VIEW ── */}
        {view === 'monthly' && (
          <>
            {/* Month navigation */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex-1 text-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  {MONTH_NAMES[monthYear.month]} {monthYear.year}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Click any day to open attendance</p>
              </div>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <MonthlyView
              year={monthYear.year}
              month={monthYear.month}
              classes={filteredClasses}
              sessionsMap={broadSessions}
              onSelectDay={handleSelectDay}
              displayTimezone={displayTimezone}
            />
          </>
        )}

      </div>

      {/* Reschedule modal */}
      {rescheduleModal && (
        <RescheduleModal
          cls={rescheduleModal.cls}
          session={rescheduleModal.session}
          onConfirm={handleRescheduleClass}
          onClose={() => setRescheduleModal(null)}
          saving={saving}
          conflictError={rescheduleModal.conflictError ?? null}
          displayTimezone={displayTimezone}
        />
      )}

      {/* Substitute modal */}
      {substituteModal && (
        <SubstituteModal
          session={substituteModal.session}
          cls={substituteModal.cls}
          teachers={teachers}
          onConfirm={(teacherId, teacherName) => {
            handleAssignSubstitute(substituteModal.cls, substituteModal.session, teacherId, teacherName);
            setSubstituteModal(null);
          }}
          onClose={() => setSubstituteModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

export default AttendanceManager;